import type { CSSProperties, ReactNode } from 'react';
import type { InvestmentRow } from '@/types';
import { formatRange, publicInvestmentProduct } from '@/editor/investmentProduct';
import { formatCurrencyRange, formatProductPrice } from '@/lib/format';
import type { PublicProductPrice } from '../../../../backend/src/utils/productInvestment';
import InvestmentProductDescription from './InvestmentProductDescription';

export function InvestmentProductPublicContent({ title, price, currency, hoursVisible, pricesVisible = true, widths, titleAdornment }: {
  title: string; price: PublicProductPrice; currency: string; hoursVisible: boolean;
  /** Uit wanneer de tabel alleen het totaal toont: dan ook geen uren of bedragen bij het product. */
  pricesVisible?: boolean;
  widths?: { hourlyRate?: number; hours?: number; subtotal?: number };
  titleAdornment?: ReactNode;
}) {
  const money = (cents: number, maxCents?: number) => formatCurrencyRange(cents / 100, maxCents === undefined ? null : maxCents / 100, currency);
  const hours = (value: number) => value.toLocaleString('nl-NL', { maximumFractionDigits: 6 });
  const showHours = hoursVisible && pricesVisible;
  const lineStyle: CSSProperties = {
    display: 'grid', gridTemplateColumns: !pricesVisible ? 'minmax(0,1fr)' : showHours
      ? 'minmax(0,1fr) ' + (widths?.hourlyRate ?? 100) + 'px ' + (widths?.hours ?? 80) + 'px ' + (widths?.subtotal ?? 120) + 'px'
      : 'minmax(0,1fr) ' + (widths?.subtotal ?? 140) + 'px',
    alignItems: 'start', gap: 0, padding: '8px 12px', lineHeight: '20px',
  };
  return <div className="investment-product-public">
    <div style={{ ...lineStyle, fontWeight: price.lines.length > 0 ? 600 : 400 }}>
      {titleAdornment ? <div className="investment-product-title-with-controls">
        <InvestmentProductDescription title={title} descriptionHtml={price.descriptionHtml} />
        {titleAdornment}
      </div> : <InvestmentProductDescription title={title} descriptionHtml={price.descriptionHtml} />}
      {showHours && <><span/><span/></>}
      {pricesVisible && <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {formatProductPrice({ minCents: price.totalCents, maxCents: price.maxTotalCents, priceText: price.priceText }, currency)}
      </span>}
    </div>
    {price.lines.map((line, index) => <div key={index} style={lineStyle}>
      <span>{line.description}</span>
      {showHours && <>
        <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{line.rateCents === undefined ? '' : money(line.rateCents)}</span>
        <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{line.hours === undefined ? '' : formatRange(line.hours, line.maxHours, hours)}</span>
      </>}
      {pricesVisible && <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(line.totalCents, line.maxTotalCents)}</span>}
    </div>)}
  </div>;
}

/** Same customer projection for preview, public web and printed PDF. */
export default function InvestmentProductPublicRow({ row, currency, hoursVisible, pricesVisible = true, cellStyle }: {
  row: InvestmentRow; currency: string; hoursVisible: boolean; pricesVisible?: boolean; cellStyle: CSSProperties;
}) {
  const price = row.investmentProduct ? publicInvestmentProduct(row.investmentProduct) : row.productPrice!;
  return <tr data-investment-row={row.id}>
    <td colSpan={!pricesVisible ? 1 : hoursVisible ? 4 : 2} style={{ ...cellStyle, padding: 0 }}>
      <InvestmentProductPublicContent title={row.title} price={price} currency={currency} hoursVisible={hoursVisible} pricesVisible={pricesVisible}/>
    </td>
  </tr>;
}
